<template>
    <div class="detail-page">
        <!-- 加载状态 -->
        <el-loading v-if="loading" :text="'加载中...'" fullscreen></el-loading>

        <!-- 错误提示 -->
        <el-alert v-if="error" type="error" description="数据加载失败，请重试" show-icon closable
            style="margin: 20px;"></el-alert>

        <!-- 详情内容（左右布局） -->
        <div class="detail-container" v-if="company && !loading && !error">
            <!-- 左侧内容区 -->
            <div class="left-panel">
                <!-- 2.1 公司名称 -->
                <h1 class="company-title">{{ company.investmentCompany }}</h1>

                <!-- 2.2 所属类别 -->
                <el-tag class="company-type" type="primary">{{ company.type }}</el-tag>

                <!-- 2.3 参考利率、申请额度、申请期限（带logo小卡片） -->
                <div class="info-cards">
                    <el-card class="info-card">
                        <div slot="header" class="card-header">
                            <img src="/images/investmentCompany/little_logo0.png" alt="利率图标" class="card-icon">
                            <span>参考利率</span>
                        </div>
                        <div class="card-value">{{ company.referenceInterestRate || '暂无数据' }}</div>
                    </el-card>

                    <el-card class="info-card">
                        <div slot="header" class="card-header">
                            <img src="/images/investmentCompany/little_logo1.png" alt="额度图标" class="card-icon">
                            <span>申请额度</span>
                        </div>
                        <div class="card-value">{{ company.applicationAmount }}</div>
                    </el-card>

                    <el-card class="info-card">
                        <div slot="header" class="card-header">
                            <img src="/images/investmentCompany/little_logo2.png" alt="期限图标" class="card-icon">
                            <span>申请期限</span>
                        </div>
                        <div class="card-value">{{ company.applicationTerm }}</div>
                    </el-card>
                </div>

                <!-- 2.4 产品简介 -->
                <div class="section">
                    <h3 class="section-title">
                        <i class="el-icon-info"></i> 产品简介
                    </h3>
                    <p class="section-content">该投资产品由{{ company.investmentCompany }}提供，专注于{{
                        company.investmentAreas.substring(0, 50) }}等领域，为符合条件的企业提供资金支持和资源对接服务。</p>
                </div>

                <!-- 2.5 机构介绍 -->
                <div class="section">
                    <h3 class="section-title">机构介绍</h3>
                    <p class="section-content">{{ company.institutionIntroduction }}</p>
                </div>

                <!-- 2.6 投资领域 -->
                <div class="section">
                    <h3 class="section-title">投资领域</h3>
                    <p class="section-content">{{ company.investmentAreas }}</p>
                </div>

                <!-- 2.7 适用客户 -->
                <div class="section">
                    <h3 class="section-title">适用客户</h3>
                    <p class="section-content">{{ company.targetClients }}</p>
                </div>
            </div>

            <!-- 右侧内容区 -->
            <div class="right-panel">
                <!-- 3.1 公司Logo -->
                <div class="logo-container">
                    <img :src="company.logo" :alt="company.investmentCompany + '的logo'" class="company-logo"
                        @error="handleLogoError">
                </div>

                <!-- 3.2 成立时间、所在地 -->
                <div class="info-list">
                    <div class="info-item">
                        <span class="info-label">成立时间：</span>
                        <span class="info-value">{{ company.establishmentYear }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">所在地：</span>
                        <span class="info-value">{{ company.location }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">所在区域：</span>
                        <span class="info-value">{{ company.serviceRegion }}</span>
                    </div>
                </div>

                <!-- 支持方式 -->
                <div class="section right-section">
                    <h3 class="section-title">支持方式</h3>
                    <div class="method-tags">
                        <el-tag v-for="(method, idx) in company.method" :key="idx" :type="getTagType(idx)" size="small">
                            {{ method }}
                        </el-tag>
                    </div>
                </div>

                <!-- 联系方式 -->
                <div class="section right-section">
                    <h3 class="section-title">
                        <i class="el-icon-phone"></i> 联系方式
                    </h3>
                    <div class="contact-info">
                        <p>客服电话：{{ company.phone }}</p>
                        <p>联系邮箱：{{ company.email }}</p>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'InvestmentCompanyDetail',
    props: {
        // 通过路由props接收id参数
        id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            company: null,
            loading: false,
            error: null,
            defaultLogo: 'https://picsum.photos/id/20/200/200' // 默认logo地址
        };
    },
    methods: {
        // 获取详情数据
        async getCompanyDetail() {
            try {
                this.loading = true;
                // 实际项目中替换为真实接口，通过id查询详情
                const response = await this.$httpTemp.get(`/mock/investmentCompany.json`);
                // 从列表数据中筛选当前id对应的公司（实际项目直接请求单条详情接口）
                this.company = response.find(item => item.id === this.id) || null;
                if (!this.company) {
                    this.error = '未找到该公司信息';
                }
            } catch (err) {
                this.error = '数据加载失败';
                console.error('详情加载失败:', err);
            } finally {
                this.loading = false;
            }
        },
        // 标签类型切换
        getTagType(index) {
            const types = ['success', 'info', 'warning', 'primary', 'danger'];
            return types[index % types.length];
        },
        // 处理logo加载失败
        handleLogoError(e) {
            e.target.src = this.defaultLogo;
        }
    },
    mounted() {
        // 页面加载时获取详情
        this.getCompanyDetail();
    }
};
</script>

<style scoped>
.detail-page {
    padding: 20px;
    max-width: 1400px;
    margin: 0 auto;
}

.detail-container {
    display: flex;
    gap: 30px;
    margin-top: 20px;
}

/* 左侧面板样式 */
.left-panel {
    flex: 3;
    /* 左侧占3份 */
    background: rgba(173, 216, 230, 0.3);
    /* 淡蓝色半透明 */
    border-radius: 12px;
    padding: 30px;
    box-sizing: border-box;
}

.company-title {
    margin: 0 0 20px 0;
    font-size: 28px;
    color: #333;
    font-weight: 600;
}

.company-type {
    margin-bottom: 30px;
    padding: 20px 40px;
    font-size: 30px;
    border: 2px solid #165DFF;
}

.info-cards {
    display: flex;
    gap: 15px;
    margin-bottom: 40px;
}

.info-card {
    flex: 1;
    text-align: center;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-weight: bold;
    background-color: rgba(255, 255, 255, 0.7);
}

.card-icon {
    height: 50px;
}

.card-value {
    font-size: 20px;
    font-weight: bold;
    padding: 15px 0;
    color: #f35308;
    background-color: rgba(255, 255, 255, 0.5);
}

/* 通用区块样式 */
.section {
    margin-bottom: 30px;
}

.section-title {
    font-size: 18px;
    margin: 0 0 15px 0;
    color: #34495e;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 5px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.section-content {
    font-size: 15px;
    line-height: 1.8;
    color: #333;
    margin: 0;
    text-align: justify;
    padding: 5px 0;
}

/* 右侧面板样式 */
.right-panel {
    flex: 1;
    /* 右侧占1份 */
    background: rgba(173, 216, 230, 0.3);
    /* 淡蓝色半透明 */
    border-radius: 12px;
    padding: 30px;
    box-sizing: border-box;
}

.logo-container {
    text-align: center;
    margin-bottom: 30px;
    padding: 20px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
}

.company-logo {
    width: 100%;
    max-width: 200px;
    height: auto;
    border-radius: 8px;
    border: 1px solid #ddd;
    padding: 10px;
    background: #fff;
    box-sizing: border-box;
}

.info-list {
    margin-bottom: 30px;
    background: rgba(255, 255, 255, 0.7);
    padding: 15px;
    border-radius: 8px;
}

.info-item {
    margin-bottom: 15px;
    display: flex;
    align-items: flex-start;
}

.info-item:last-child {
    margin-bottom: 0;
}

.info-label {
    font-weight: bold;
    color: #555;
    min-width: 80px;
}

.info-value {
    color: #333;
    flex: 1;
}

.right-section {
    background: rgba(255, 255, 255, 0.7);
    padding: 15px;
    border-radius: 8px;
}

.method-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.contact-info {
    line-height: 1.8;
}


/* 响应式调整 */
@media (max-width: 992px) {
    .detail-container {
        flex-direction: column;
    }

    .info-cards {
        flex-direction: column;
    }
}
</style>